import { clamp, isEmpty, escapeRegExp, sortBy } from 'lodash';
import { getTenantConfig } from './tenant';

export const useResponsiveTable = (width, itemCount) => {
  const MOBILE_MAX_WIDTH = 500;
  const TABLET_MAX_WIDTH = 1023;

  return (width > TABLET_MAX_WIDTH && itemCount > 9) ||
    (width > MOBILE_MAX_WIDTH && width <= TABLET_MAX_WIDTH && itemCount > 6) ||
    width <= MOBILE_MAX_WIDTH;
};

export const isMobileView = (surveyQuestions, windowWidth) => {
  if (!surveyQuestions) return false;
  return useResponsiveTable(windowWidth, surveyQuestions.length);
};

export const hasComment = (questionId, answers) => {
  if (!answers) return false;
  const answer = answers.find(({id}) => id === questionId);
  return answer && answer.comment ? true : false;
};

export const anyQuestionHasComment = (questions, answers) => {
  return questions.filter(question => hasComment(question.id, answers)).length > 0;
};

export const loadAutosaveConfigs = () => {
  const autosaveMode = getTenantConfig('AUTOSAVE_MODE');
  if (autosaveMode === 'Manual') return [null, null];
  const autosaveIntervalConfig = getTenantConfig('AUTOSAVE_SURVEY_INTERVAL');
  // Default and min value is saving every 5 seconds, max is 60 seconds
  const autosaveIntervalMs = isNaN(autosaveIntervalConfig) ? 5000 : clamp(autosaveIntervalConfig, 5, 60) * 1000;
  // This will equal ~5 minutes worth of autosaving. To avoid infinitely delaying session timeout on error, once this 
  // limit is hit, we will only resume autosave if input is changed further or a manual save is successful
  const maxFailedAttempts = Math.floor(300000 / autosaveIntervalMs);
  
  return [autosaveIntervalMs, maxFailedAttempts];
};

export const getCommentForStudentQuestion = (studentAnswers, questionId) => {
  return studentAnswers ? studentAnswers.find(({id: answerQuestionId}) => questionId === answerQuestionId) : null;
};

// Check if there is a comment in each response, add with blank comment if not
export const getResponsesForStudent = (studentQuestions, studentAnswers) => {
  let responsesForStudent = [];
  studentQuestions.forEach(questionId => {
    const questionAnswer = getCommentForStudentQuestion(studentAnswers, questionId);
    if (questionAnswer && !isEmpty(questionAnswer.comment)) {
      responsesForStudent.push({id: questionAnswer.id, comment: questionAnswer.comment});
    }
    else responsesForStudent.push({id: questionId, comment: ''});
  });
  return responsesForStudent;
};

export const getSurveyResponseJsonBody = (surveyId, checkedQuestionsByStudent, surveyAnswers, answersByStudent) => {
  if ( !surveyAnswers ) return null;
  const jsonResponse = {"surveyResponseId": surveyId, "answers": {}};
  surveyAnswers.map(({id: studentId}) => {
    // If the student has survey feedback, add it to the jsonResponse under answers
    jsonResponse.answers[studentId] = checkedQuestionsByStudent[studentId] 
      ? getResponsesForStudent(checkedQuestionsByStudent[studentId], answersByStudent[studentId]) 
      : [];
  });
  return JSON.stringify(jsonResponse);
};

export const hideSaveDraftButton = () => {
  const autosaveMode = getTenantConfig('AUTOSAVE_MODE');
  return autosaveMode !== 'Both' && autosaveMode !== 'Manual';
};

export const studentHasError = (checkedQuestions, answers) => {
  // If nothing checked, can't be any error
  if ( !checkedQuestions || !answers ) return false;

  const invalidAnswers = answers
    .filter(answer => checkedQuestions.includes(answer.id))
    .filter(answer => answer.shouldValidate && !answer.isValid);

  return invalidAnswers.length > 0;
};

export const checkLastViewedSurvey = () => {
  const lastViewedSurveyId = parseInt(localStorage.getItem('lastViewedSurveyId'));
  return isNaN(lastViewedSurveyId) ? null : lastViewedSurveyId;
};

export const setLastViewedSurvey = (surveyId) => {
  if (surveyId) localStorage.setItem('lastViewedSurveyId', surveyId);
};

export const clearLastViewedSurvey = () => {
  localStorage.removeItem('lastViewedSurveyId');
};

export const sortAnswersByStudentNames = surveyAnswers => {
  if ( !surveyAnswers ) return surveyAnswers;
  return sortBy(surveyAnswers, (surveyedStudent) => String(surveyedStudent.name).toUpperCase());
};

export const searchForStudents = (value, studentList) => {
    if( !value ) return studentList;
    const searchWords = value.trim().split(/[\s,]+/, 2) ;
    if (searchWords.length > 1) {
        //Only matching the first name and last name pattern in any order
        const fnameRegEx = new RegExp(escapeRegExp(searchWords[0]), 'i');
        const lnameRegEx = new RegExp(escapeRegExp(searchWords[1]), 'i');
        return studentList.filter(result => fnameRegEx.test(result.name) && lnameRegEx.test(result.name));
    } else {
        const searchText = new RegExp(escapeRegExp(value.trim()), 'i');
        const isMatch = result => searchText.test(result.name)
            || searchText.test(result.username)
            || searchText.test(result.preferredEmail)
            || searchText.test(result.studentId)
            || searchText.test(result.email);
        return studentList.filter(isMatch);
    }
};
